import { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { CalendarIcon } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { Heading } from "../../ui/heading";
import { Button } from "../../ui/button";
import { Separator } from "../../ui/separator";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../../ui/form";
import { Input } from "../../ui/input";
import Sidebar from "../../DashboardNavigation/sidebar";
import Header from "../../DashboardNavigation/header";
import { AlertModal } from "../../modal/alert-modal";
import BreadCrumb from "../../breadcrumb";
import {
  EventFormValues,
  eventSchema,
  UpdateEventFormValues,
  updateEventSchema,
} from "./event-zod-schema";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import { Calendar } from "../../ui/calendar";
import { cn } from "../../../lib/utils";
import {
  getEventById,
  handleCreateEvent,
  handleUpdateEvent,
} from "../../../API/Events/eventService";
import QuillEditor from "../../../lib/quill/quill";
import FileUpload from "../../FileUpload/FileUpload";
import { formatDateToFrenchLocale } from "../../../utils/dateUtils";

export const EventForm: React.ComponentType<any> = () => {
  const uploadUrl = process.env.REACT_APP_FILE_UPLOAD;
  const { id } = useParams();
  const initialData = id ? parseInt(id) : undefined;
  const router = useNavigate();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const title = initialData ? "Modifier l'événement" : "Ajouter un événement";
  const description = initialData
    ? "Modifier l'événement"
    : "Ajouter un événement";
  const action = initialData ? "Mise à jour" : "Ajouter";

  const form = useForm<EventFormValues | UpdateEventFormValues>({
    resolver: zodResolver(initialData ? updateEventSchema : eventSchema),
    mode: "onChange",
  });

  const onSubmit = async () => {
    setLoading(true);
    const formData = form.getValues();
    try {
      if (initialData) {
        await handleUpdateEvent(initialData, formData, router);
      } else {
        await handleCreateEvent(formData, form.setValue, eventSchema, router);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  const onDelete = async () => {
    try {
      setLoading(true);

      router(`/admin-dashboard/evenements`);
    } catch (error: any) {
    } finally {
      setLoading(false);
      setOpen(false);
    }
  };

  const breadcrumbItems = [
    { title: "Événements", link: "/admin-dashboard/evenements/" },
    {
      title: initialData ? "Modifier" : "Ajouter",
      link: "/admin-dashboard/evenements/ajouter",
    },
  ];

  const [data, setData] = useState<any>();
  useEffect(() => {
    const fetchData = async () => {
      if (initialData) {
        const eventData = await getEventById(initialData);
        setData(eventData);
        form.reset({
          title: eventData.title,
          tag: eventData.tag,
          description: eventData.description,
          date: eventData.date ? new Date(eventData.date) : undefined,
          image: eventData.image
            ? [
                {
                  fileName: eventData.image,
                  name: eventData.image,
                  fileSize: 0,
                  size: 0,
                  key: eventData.image,
                },
              ]
            : [],
        });
      }
    };

    if (initialData) {
      fetchData();
    }
  }, [initialData]);

  return (
    <>
      <AlertModal
        isOpen={open}
        onClose={() => setOpen(false)}
        onConfirm={onDelete}
        loading={loading}
        description="Évènement supprimé avec succè"
      />

      <Header />
      <div className="flex h-screen ">
        <Sidebar />
        <div className="w-full p-10">
          <BreadCrumb items={breadcrumbItems} />
          <div className="flex items-center justify-between">
            <Heading title={title} description={description} />
          </div>
          <Separator />
          <Form {...form}>
            <form
              className="w-full mt-5 space-y-8"
              onSubmit={form.handleSubmit(onSubmit)}
            >
              <div className="gap-8 md:grid md:grid-cols-3">
                <FormField
                  control={form.control}
                  name="title"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Titre </FormLabel>
                      <FormControl>
                        <Input
                          disabled={loading}
                          placeholder="Titre..."
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="tag"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Catégorie </FormLabel>
                      <FormControl>
                        <Input
                          disabled={loading}
                          placeholder="Catégorie..."
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="date"
                  render={({ field }) => (
                    <FormItem className="flex flex-col mt-3">
                      <FormLabel>Date de l'événement</FormLabel>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant={"outline"}
                            className={cn(
                              "w-[280px] justify-start text-left font-normal",
                              !field.value && "text-muted-foreground"
                            )}
                          >
                            <CalendarIcon className="w-4 h-4 mr-2" />
                            {field.value ? (
                              formatDateToFrenchLocale(field.value)
                            ) : (
                              <span>Date début</span>
                            )}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                          <Calendar
                            mode="single"
                            selected={field.value}
                            onSelect={(date) => {
                              field.onChange(date);
                            }}
                          />
                        </PopoverContent>
                      </Popover>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <QuillEditor
                        value={field.value || ""}
                        onChange={(value: string) => {
                          field.onChange(value);
                          form.trigger("description");
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {data?.image && (
                <div className="mt-4 mb-4">
                  <img
                    src={`${uploadUrl}/events/${data.image}`}
                    alt="Old Image Preview"
                    className="w-10 h-10 h-auto"
                  />
                </div>
              )}
              <FormField
                control={form.control}
                name="image"
                render={() => (
                  <FormItem>
                    <FormControl>
                      <FileUpload
                        onChange={(files) => {
                          const mappedFiles = files.map((file) => ({
                            key: file.id,
                            fileName: file.fileName,
                            name: file.fileName,
                            fileSize: parseInt(file.fileSize),
                            size: parseInt(file.fileSize),
                            file: file.file,
                          }));
                          form.setValue("image", mappedFiles);
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="flex space-x-5">
                <div>
                  <Button
                    disabled={loading}
                    isLoading={loading}
                    className="ml-auto"
                    type="submit"
                  >
                    {action}
                  </Button>
                </div>
                <div>
                  <Button
                    onClick={() => router(-1)}
                    disabled={loading}
                    isLoading={loading}
                    className="ml-auto bg-red-500"
                    type="button"
                  >
                    {"Annuler"}
                  </Button>
                </div>
              </div>
            </form>
          </Form>
        </div>
      </div>
    </>
  );
};
